/**
 * Replaces TipTap mention spans (inserted via '@' trigger) with an inline
 * variable indicator: the value rendered as plain text, followed by a small
 * badged icon to the right.
 *
 * Variables are resolved by GUID (the span's `data-id`) against the flat,
 * GUID-keyed `vars` map, so a variable rename never breaks resolution. The
 * span's text content is used only as the not-set display fallback.
 *
 * An unset variable renders as nothing at all: someone running the flow who
 * skipped the earlier question should see a blank, not the `@label` macro that
 * exists only for the author's benefit. `showUnsetLabel` opts back into that
 * fallback for the editor canvas, where variables resolve to '' by design and
 * hiding them would leave the author looking at empty space.
 *
 * TipTap outputs:
 *   <span data-type="mention" data-id="<guid>" class="mention">@label</span>
 */
import DOMPurify from 'dompurify';

/**
 * Flow content is authored as HTML and rendered through
 * dangerouslySetInnerHTML in four places, one of which is the public
 * `/shared/:token` route that requires no authentication. Unsanitised, an
 * editor could inject script that runs on the portal origin for anyone opening
 * the link, with access to localStorage and the JWT.
 *
 * Sanitising here rather than at each render site: all four paths funnel
 * through renderRichTextWithVars, so this is the one choke point that cannot
 * be forgotten when a fifth render site is added.
 *
 * Applied to the author's HTML *before* mention substitution, so the badge
 * markup this module generates itself is never mangled, and variable values
 * keep their existing escaping.
 */
function sanitizeAuthorHtml(html: string): string {
  // Without a DOM, DOMPurify reports isSupported false and passes input
  // through untouched. Escaping instead means a non-browser caller renders
  // inert text rather than live markup — fail closed, not open.
  if (!DOMPurify.isSupported) {
    return html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  return DOMPurify.sanitize(html, {
    // data-type / data-id carry the mention GUIDs this module resolves.
    ALLOW_DATA_ATTR: true,
  });
}

export function renderRichTextWithVars(
  html: string,
  vars: Record<string, any> = {},
  {
    showUnsetLabel = false,
    labels = {},
  }: {
    showUnsetLabel?: boolean;
    /**
     * Current variable names keyed by GUID.
     *
     * A mention's visible label is baked into the stored HTML when it is
     * inserted, so it is a snapshot of the name at that moment. Only the GUID
     * is durable. Without this map a rename never reaches existing content and
     * authors are shown a name that no longer exists.
     */
    labels?: Record<string, string>;
  } = {},
): string {
  if (!html) return html;

  const safeHtml = sanitizeAuthorHtml(html);

  const escapeHtml = (value: string) =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  return safeHtml.replace(
    /<span[^>]*data-type="mention"[^>]*>([^<]*)<\/span>/g,
    (match, textContent: string) => {
      const idMatch = match.match(/data-id="([^"]*)"/);
      const varId = idMatch ? idMatch[1] : '';
      // Strip either trigger char so content saved before the #→@ switch
      // still renders a clean not-set fallback.
      // Prefer the variable's current name; fall back to the label baked into
      // the span for content whose variable has since been deleted.
      const bakedLabel = textContent.replace(/^[#@]/, '').trim();
      const label = (varId && labels[varId]) || bakedLabel;
      const rawValue = varId ? vars[varId] : undefined;
      const hasValue = rawValue != null && rawValue !== '';

      // Drop the mention entirely — no label, no badge — so the surrounding
      // sentence closes up around the missing answer.
      if (!hasValue && !showUnsetLabel) return '';

      const rawDisplay = hasValue ? String(rawValue) : `@${label}`;
      const display = escapeHtml(rawDisplay);
      const tooltip = hasValue
        ? `Variable: ${label} = ${rawDisplay}`
        : `Variable: ${label} (not set)`;

      const badgeColor = hasValue ? '#2f9e44' : '#868e96';

      const iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>`;

      return [
        display,
        `<span`,
        ` class="var-marker"`,
        ` title="${escapeHtml(tooltip)}"`,
        ` style="display:inline-flex;align-items:center;justify-content:center;width:13px;height:13px;border-radius:50%;background:${badgeColor};color:#fff;font-size:8px;vertical-align:super;margin-left:2px;cursor:default;line-height:1;flex-shrink:0;"`,
        `>${iconSvg}</span>`,
      ].join('');
    },
  );
}

/**
 * Elements that are content in their own right, with no text of their own.
 *
 * Without this, stripping tags reduces `<img src="...">` to an empty string and
 * the whole block is judged empty — which silently discarded image-only rich
 * text: the canvas showed the placeholder, the simulator rendered nothing, and
 * reopening the page editor initialised the editor with '' and lost the image.
 *
 * `br` is deliberately absent: Tiptap represents an empty paragraph as
 * `<p><br></p>`, so counting it would make every blank block look non-empty.
 */
const MEDIA_TAG = /<\s*(img|table|iframe|video|audio|hr|picture|source|embed|object)\b/i;

export function isEmptyRichText(html?: string | null): boolean {
  if (!html) return true;

  if (MEDIA_TAG.test(html)) return false;

  const normalized = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

  return normalized === '' || normalized === 'Enter your rich text here...';
}

export function renderTextWithVars(
  text: string,
  vars: Record<string, any> = {},
): string {
  if (!text) return text;

  const resolveValue = (varName: string) => {
    if (vars[varName] != null && vars[varName] !== '') return vars[varName];
    if (vars.session?.[varName] != null && vars.session[varName] !== '') return vars.session[varName];
    if (vars.global?.[varName] != null && vars.global[varName] !== '') return vars.global[varName];
    return undefined;
  };

  return text.replace(/(^|[^\w])@([a-zA-Z][a-zA-Z0-9_]*)/g, (match, prefix: string, varName: string) => {
    const value = resolveValue(varName);
    if (value == null || value === '') return match;
    return `${prefix}${String(value)}`;
  });
}

/** CSS to inject for variable markers in editor context (TipTap .mention class). */
export const VAR_BUBBLE_CSS = `
.rich-text-render img {
  /* Widths set in the editor arrive as an inline style. max-width keeps an
     image wider than its container (a narrow simulator frame, a phone) scaling
     down rather than overflowing, and height:auto preserves the aspect ratio. */
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

.var-marker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  font-size: 8px;
  vertical-align: super;
  margin-left: 2px;
  cursor: default;
  line-height: 1;
  flex-shrink: 0;
}

.rich-text-render table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 12px 0;
}

.rich-text-render table td,
.rich-text-render table th {
  border: 1px solid #DEE2E6;
  padding: 8px;
  min-width: 1em;
  vertical-align: top;
}

.rich-text-render table th {
  background-color: #F8F9FA;
  font-weight: 600;
}
`;
